import {
  type FilterDefinition,
  type TableQueryHandlers,
  type TableQueryParamsResult,
  type TableQueryState,
  useTableQueryParams,
} from "hooks/useTableQueryParams";

import { defaultSorting } from "../constants";
import { columnToParam } from "../getColumnOptions";
import { FiltersEnum } from "../types";

const DEFAULT_PAGE_SIZE = 10;
const DATE_FROM_PARAM = `${FiltersEnum.AddedDate}From`;
const DATE_TO_PARAM = `${FiltersEnum.AddedDate}To`;

const parseAccountType = (value: string): number | undefined => {
  if (!value) {
    return undefined;
  }

  const parsed = Number.parseInt(value, 10);

  return Number.isFinite(parsed) ? parsed : undefined;
};

const orderPropToColumn = Object.entries(columnToParam).reduce<Record<string, string>>((acc, [column, prop]) => {
  acc[prop] = column;
  return acc;
}, {});

const filterDefinitions: ReadonlyArray<FilterDefinition<FiltersEnum>> = [
  {
    key: FiltersEnum.Tags,
    type: "array",
  },
  {
    key: FiltersEnum.AccountType,
    type: "array",
    parse: parseAccountType,
  },
  {
    key: FiltersEnum.AddedDate,
    type: "range",
    fromParam: DATE_FROM_PARAM,
    toParam: DATE_TO_PARAM,
  },
];

const sortingKeys = Object.values(columnToParam);
const defaultSortBy = columnToParam[defaultSorting.columnName] || defaultSorting.columnName;

const accountsQueryConfig = {
  defaultPageSize: DEFAULT_PAGE_SIZE,
  filters: filterDefinitions,
  sorting: {
    valid: sortingKeys,
    defaultSortBy,
    defaultDirection: defaultSorting.direction,
    sortKeyToColumnName: orderPropToColumn,
    fallbackColumnName: defaultSorting.columnName,
  },
} as const;

export type AccountsQueryState = TableQueryState<FiltersEnum>;
export type AccountsQueryHandlers = TableQueryHandlers<FiltersEnum>;
export type AccountsQueryParamsResult = TableQueryParamsResult<FiltersEnum>;

export const useAccountsQueryParams = (): AccountsQueryParamsResult => {
  return useTableQueryParams<FiltersEnum>(accountsQueryConfig);
};

export default useAccountsQueryParams;
